import { useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Video,
  PoundSterling,
  Clock,
  ShieldCheck,
  PlayCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import confetti from "canvas-confetti";
import { SEOHead } from "@/components/SEOHead";

const TRACKING_ID = "tk_4228c869be4b43a6af25f3931923e54d";
const LOCATION_ID = "nsp8u9BgTJB40BULOWfw";

const PRICING_TIERS = [
  {
    min: 1,
    max: 5,
    perVideo: 30,
    label: "1–5 Videos",
  },
  {
    min: 6,
    max: 10,
    perVideo: 26,
    label: "6–10 Videos",
  },
  {
    min: 11,
    max: Infinity,
    perVideo: 22,
    label: "11+ Videos",
  },
];

const EXAMPLES = [
  {
    title: "Branded Van Reveal",
    description:
      "Your logo and van brought to life in a dynamic, attention-grabbing reveal — perfect for social media.",
  },
  {
    title: "Before & After Clean",
    description:
      "A satisfying AI-generated transformation showing sparkling clean windows on a residential property.",
  },
  {
    title: "Owner Introduction",
    description:
      "A friendly, professional intro featuring your likeness and branding to build trust with new customers.",
  },
  {
    title: "Seasonal Promo",
    description:
      "A timely, branded promo video for spring cleans, gutter clearance season, or conservatory roof cleaning.",
  },
  {
    title: "Customer Testimonial Style",
    description:
      "An AI-generated scene of a happy customer reaction, branded with your business details.",
  },
  {
    title: "Area Coverage Showcase",
    description:
      "A visual flythrough of the towns you cover, branded with your logo and contact details.",
  },
];

const getTier = (qty: number) => {
  return (
    PRICING_TIERS.find((t) => qty >= t.min && qty <= t.max) ??
    PRICING_TIERS[PRICING_TIERS.length - 1]
  );
};

const AiVideo = () => {
  const { toast } = useToast();
  const [quantity, setQuantity] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const tier = getTier(quantity);
  const total = quantity * tier.perVideo;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());

    const trackingPayload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: "AI Video Offer Form",
      formData: {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone,
        organization: data.businessName,
        "contact.video_quantity": String(quantity),
        "contact.video_price_per": String(tier.perVideo),
        "contact.video_total": String(total),
        "contact.video_brief": data.brief,
      },
      formLabels: {
        first_name: "First Name",
        last_name: "Last Name",
        email: "Email Address",
        phone: "Phone Number",
        organization: "Business Name",
        "contact.video_quantity": "Number of Videos",
        "contact.video_price_per": "Price Per Video",
        "contact.video_total": "Total Price",
        "contact.video_brief": "Video Brief / Notes",
      },
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: TRACKING_ID,
      locationId: LOCATION_ID,
      sessionId: crypto.randomUUID(),
      properties: {
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
          ? "mobile"
          : "desktop",
        tags: ["ai_video_offer"],
      },
    };

    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        version: "2021-07-28",
      },
      body: JSON.stringify(trackingPayload),
    }).catch(() => {});

    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      confetti({
        particleCount: 120,
        spread: 75,
        origin: { y: 0.6 },
        colors: ["#004d8c", "#fe5240", "#ffffff"],
      });
    }, 900);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-foreground flex flex-col">
      <SEOHead
        title="AI-Created Window Cleaning Videos | Clean Marketing"
        description="Get professional, AI-generated window cleaning videos branded with your own logo, vans, and likeness — at a fraction of the usual cost. An exclusive offer for Clean Marketing clients."
        canonical="/aivideo"
      />
      <Header />

      <main className="flex-grow">
        {/* Hero */}
        <section className="relative py-20 sm:py-28 bg-secondary text-white overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-secondary/50 rounded-3xl transform rotate-3 opacity-20 blur-xl"></div>
          <div className="container mx-auto px-4 max-w-5xl relative z-10 text-center">
            <div className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-sm font-medium text-white mb-6 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 mr-2 text-primary" /> Exclusive
              Client Offer
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight mb-6">
              AI-Created Window Cleaning Videos,{" "}
              <span className="text-primary">Branded For Your Business</span>
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed max-w-3xl mx-auto">
              Get professional, AI-generated window cleaning videos featuring
              your own logo, vans, and likeness — at a fraction of the usual
              cost. Use them in your marketing, on social media, or wherever you
              like.
            </p>
            <Button
              size="lg"
              className="text-base h-14 px-10 bg-primary text-primary-foreground hover:bg-[#e04825] transition-colors shadow-lg"
              asChild
            >
              <a href="#order">
                Claim Your Videos <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </section>

        {/* The Offer Explanation */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl mb-4">
                Why we're offering this
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                A genuine, time-limited opportunity for our clients.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-slate-200 shadow-md bg-white">
                <CardContent className="p-8">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-6">
                    <PoundSterling className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-secondary mb-3">
                    A fraction of the usual cost
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    If you were to create these AI videos yourself, you'd
                    typically pay around <strong>£18 per video</strong> — and
                    that includes the ones that don't come out well, with
                    mistakes or lower quality. We're offering them to our
                    clients from as little as <strong>£22 each</strong>, fully
                    branded and quality-checked.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-md bg-white">
                <CardContent className="p-8">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-6">
                    <Clock className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-secondary mb-3">
                    How we can do this
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    We're purchasing a very expensive unlimited plan for{" "}
                    <strong>7 days only</strong>. We're using this window to
                    offset the cost of that plan — and we'd love for our clients
                    to make the most of AI video at a genuinely reduced discount
                    while we can.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 bg-primary/5 border border-primary/20 rounded-2xl p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <Video className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-secondary mb-2">
                    Coming soon: non-branded AI videos in your campaigns
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    We'll soon be implementing non-branded AI videos directly
                    into your marketing campaigns as part of the service. But if
                    you'd like <strong>branded videos</strong> — featuring your
                    logo, vans, and likeness — alongside those, you'll need to
                    purchase them through this offer.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Example Videos */}
        <section className="py-20 bg-slate-50 border-y border-slate-200">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-12">
              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
                Examples
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl mb-4">
                See what's possible
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Here are six examples of AI videos we can create for your
                business. (Placeholders shown — your videos will be fully
                branded.)
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {EXAMPLES.map((example, i) => (
                <Card
                  key={i}
                  className="border-slate-200 shadow-md bg-white overflow-hidden group"
                >
                  <div className="relative aspect-video bg-gradient-to-br from-secondary to-secondary/80 flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent opacity-60"></div>
                    <PlayCircle className="h-16 w-16 text-white/80 group-hover:scale-110 transition-transform" />
                    <span className="absolute bottom-3 left-3 text-xs font-medium text-white/70 bg-black/30 px-2 py-1 rounded">
                      Example {i + 1}
                    </span>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-secondary mb-2">
                      {example.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {example.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Tiers */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl mb-4">
                Simple, tiered pricing
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                The more videos you order, the less you pay per video. All fully
                branded with your logo, vans, and likeness.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {PRICING_TIERS.map((t, i) => {
                const isBest = i === PRICING_TIERS.length - 1;
                return (
                  <Card
                    key={i}
                    className={`relative overflow-hidden bg-white shadow-md ${
                      isBest
                        ? "border-primary ring-2 ring-primary/30"
                        : "border-slate-200"
                    }`}
                  >
                    {isBest && (
                      <div className="absolute top-0 right-0 bg-primary text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                        BEST VALUE
                      </div>
                    )}
                    <CardHeader className="text-center pb-4 pt-8">
                      <CardDescription className="text-slate-500 font-medium">
                        {t.label}
                      </CardDescription>
                      <div className="mt-4 flex justify-center items-baseline">
                        <span className="text-5xl font-extrabold text-secondary">
                          £{t.perVideo}
                        </span>
                        <span className="text-lg font-medium text-slate-500 ml-2">
                          /video
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-2 pb-8">
                      <ul className="space-y-3 mb-6">
                        {[
                          "Fully branded with your logo & vans",
                          "Your likeness where applicable",
                          "Quality-checked, no mistakes",
                          "Use anywhere — socials, ads, website",
                        ].map((feat, j) => (
                          <li
                            key={j}
                            className="flex items-start text-sm text-slate-700"
                          >
                            <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mr-2 mt-0.5" />
                            {feat}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <p className="text-center text-sm text-slate-500 mt-8 flex items-center justify-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Normally ~£18 per video (including failed attempts). You pay less
              — and we handle the duds.
            </p>
          </div>
        </section>

        {/* Order Form */}
        <section
          id="order"
          className="py-20 bg-slate-50 border-t border-slate-200"
        >
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="text-center mb-12">
              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
                Place Your Order
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl mb-4">
                Claim your AI videos
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Tell us how many videos you'd like and a brief outline of what
                you're after. We'll be in touch to confirm details and collect
                your branding assets.
              </p>
            </div>

            <Card className="border-primary/20 shadow-xl bg-white">
              <CardHeader className="pb-6 border-b border-slate-100 bg-slate-50">
                <CardTitle className="text-2xl text-secondary">
                  AI Video Order
                </CardTitle>
                <CardDescription className="text-base">
                  All fields are required.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {isSubmitted ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold text-secondary">
                      Thank You!
                    </h3>
                    <p className="text-slate-600 max-w-md">
                      Your AI video order has been received. James will be in
                      touch very soon to confirm the details and collect your
                      branding assets.
                    </p>
                    <Button
                      className="mt-4 bg-primary text-primary-foreground hover:bg-[#e04825] transition-colors"
                      asChild
                    >
                      <Link to="/">Back to Home</Link>
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          required
                          placeholder="John"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          required
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          placeholder="john@example.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          placeholder="07700 140011"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="businessName">Business Name</Label>
                      <Input
                        id="businessName"
                        name="businessName"
                        required
                        placeholder="Acme Window Cleaning"
                      />
                    </div>

                    {/* Quantity selector */}
                    <div className="space-y-3">
                      <Label htmlFor="quantity">Number of Videos</Label>
                      <div className="flex items-center gap-4">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-11 w-11 p-0 border-secondary/30 text-secondary"
                          onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                          aria-label="Decrease quantity"
                        >
                          −
                        </Button>
                        <Input
                          id="quantity"
                          name="quantity"
                          type="number"
                          min="1"
                          value={quantity}
                          onChange={(e) =>
                            setQuantity(
                              Math.max(1, parseInt(e.target.value) || 1),
                            )
                          }
                          className="w-24 text-center text-lg font-bold"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="h-11 w-11 p-0 border-secondary/30 text-secondary"
                          onClick={() => setQuantity((q) => q + 1)}
                          aria-label="Increase quantity"
                        >
                          +
                        </Button>
                        <div className="ml-auto text-right">
                          <p className="text-xs text-slate-500">
                            Tier: {tier.label} · £{tier.perVideo}/video
                          </p>
                          <p className="text-2xl font-extrabold text-secondary">
                            Total: £{total}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="brief">Video Brief / Notes</Label>
                      <textarea
                        id="brief"
                        name="brief"
                        required
                        rows={4}
                        placeholder="Tell us what you'd like your videos to show — e.g. branded van reveal, before & after clean, owner intro, seasonal promo..."
                        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 text-base bg-primary text-primary-foreground hover:bg-[#e04825] transition-colors shadow-md"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Sending..."
                        : `Claim ${quantity} Video${quantity > 1 ? "s" : ""} — £${total}`}
                    </Button>

                    <p className="text-xs text-center text-slate-500">
                      By submitting this form, you agree to let us contact you
                      regarding your AI video order. We'll confirm details and
                      collect your branding assets before any work begins.
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AiVideo;
